import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Stack,
  Link,
  Alert,
} from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";

export default function CareerPage() {
  // Display email
  const HR_EMAIL = "sasinnovates@gmail.com";

  // EmailJS ENV (CRA)
  const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
  const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
  const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

  // Debug (check once in console)
  console.log("ENV:", SERVICE_ID, TEMPLATE_ID, PUBLIC_KEY);

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    message: "",
  });

  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: "", msg: "" });

  const handleChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleResume = (e) => {
    const file = e.target.files?.[0];
    setResume(file || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", msg: "" });

    // ENV check
    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      setStatus({
        type: "error",
        msg: "EmailJS keys missing. Please check .env file and restart server.",
      });
      return;
    }

    // Basic validation
    if (!form.fullName || !form.email || !form.phone) {
      setStatus({
        type: "error",
        msg: "Please fill all required fields.",
      });
      return;
    }

    setLoading(true);

    try {
      const templateParams = {
        fullName: form.fullName,
        email: form.email,
        phone: form.phone,
        message: form.message || "-",
        resumeNote: resume
          ? `${resume.name} (file not attached)`
          : "Not provided",
        submittedAt: new Date().toLocaleString(),
      };

      await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY);

      setStatus({
        type: "success",
        msg: "✅ Application sent successfully!",
      });

      setForm({
        fullName: "",
        email: "",
        phone: "",
        message: "",
      });
      setResume(null);
    } catch (error) {
      console.error("EmailJS Error:", error);
      setStatus({
        type: "error",
        msg: `❌ Failed to send: ${error?.text || "Unknown error"}`,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fff", py: 6 }}>
      <Container maxWidth="sm">
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, textAlign: "center", mb: 3 }}
        >
          Join Our Career
        </Typography>

        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            p: 4,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, textAlign: "center", mb: 2 }}
          >
            Career Form
          </Typography>

          {status.msg && (
            <Alert
              severity={status.type === "success" ? "success" : "error"}
              sx={{ mb: 2 }}
            >
              {status.msg}
            </Alert>
          )}

          {/* ✅ FORM START */}
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Full Name"
                  fullWidth
                  required
                  value={form.fullName}
                  onChange={handleChange("fullName")}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  required
                  value={form.email}
                  onChange={handleChange("email")}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Phone"
                  fullWidth
                  required
                  value={form.phone}
                  onChange={handleChange("phone")}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  component="label"
                  variant="outlined"
                  fullWidth
                  startIcon={<UploadFileOutlinedIcon />}
                  sx={{ height: 56 }}
                >
                  {resume ? "Resume Selected" : "Upload Resume (optional)"}
                  <input
                    hidden
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleResume}
                  />
                </Button>

                {resume && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Selected: <b>{resume.name}</b>
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Message"
                  fullWidth
                  multiline
                  minRows={6}
                  value={form.message}
                  onChange={handleChange("message")}
                />
              </Grid>

              {/* ✅ SUBMIT BUTTON INSIDE FORM */}
              <Grid item xs={12} sx={{ textAlign: "center", mt: 1 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{ px: 6, py: 1.3, fontWeight: 700 }}
                >
                  {loading ? "Sending..." : "Submit"}
                </Button>
              </Grid>
            </Grid>
          </Box>
          {/* ✅ FORM END */}

          <Divider sx={{ my: 3 }} />

          <Stack alignItems="center" spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              Or send your resume directly to
            </Typography>
            <Link href={`mailto:${HR_EMAIL}`} sx={{ fontWeight: 700 }}>
              {HR_EMAIL}
            </Link>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}
